import { collectionGroup, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import { adminLearningService } from '../../Lessons/services/learningService';
import { isQuestionPage } from '../../Lessons/services/learningTypes';
import type {
    LearningLesson,
    LearningLessonQuestionPage,
    LearningSection,
} from '../../Lessons/services/learningTypes';

export interface OptionStat {
    label: string;
    count: number;
    percent: number; // 0..1, share of respondents who picked this option
    isCorrect: boolean;
}

export interface QuestionStats {
    questionUid: string;
    questionText: string;
    respondents: number;
    hasCorrectAnswer: boolean;
    options: OptionStat[];
    // Answer strings that no longer match any current option (question edited since answered)
    legacyAnswers: OptionStat[];
}

export interface LessonStats {
    lessonUid: string;
    title: string;
    type: LearningLesson['type'];
    questions: QuestionStats[];
}

export interface SectionStats {
    sectionUid: string;
    title: string;
    lessons: LessonStats[];
}

interface QuestionDef {
    section: LearningSection;
    lesson: LearningLesson;
    page: LearningLessonQuestionPage;
}

/** Preferred stored answer string for a question option (matches how the app saves answers). */
const optionLabel = (option: { text: string; aiFriendlyText?: string }): string =>
    option.aiFriendlyText?.trim() || option.text;

export class LearningQuestionsStatsService {
    private static instance: LearningQuestionsStatsService;

    static getInstance(): LearningQuestionsStatsService {
        if (!LearningQuestionsStatsService.instance) {
            LearningQuestionsStatsService.instance = new LearningQuestionsStatsService();
        }
        return LearningQuestionsStatsService.instance;
    }

    async getLearningQuestionStats(): Promise<SectionStats[]> {
        const [defs, aggregates] = await Promise.all([
            this.loadQuestionDefinitions(),
            this.aggregateAnswers(),
        ]);

        // Group definitions back into Section -> Lesson -> Question, joining aggregated counts.
        const sections = new Map<string, SectionStats>();

        for (const { section, lesson, page } of defs) {
            let sectionStats = sections.get(section.uid);
            if (!sectionStats) {
                sectionStats = { sectionUid: section.uid, title: section.title, lessons: [] };
                sections.set(section.uid, sectionStats);
            }

            let lessonStats = sectionStats.lessons.find((l) => l.lessonUid === lesson.uid);
            if (!lessonStats) {
                lessonStats = {
                    lessonUid: lesson.uid,
                    title: lesson.title,
                    type: lesson.type,
                    questions: [],
                };
                sectionStats.lessons.push(lessonStats);
            }

            lessonStats.questions.push(this.buildQuestionStats(page, aggregates.get(page.uid)));
        }

        return Array.from(sections.values());
    }

    /** Load every question page (that persists answers) across all sections/lessons. */
    private async loadQuestionDefinitions(): Promise<QuestionDef[]> {
        const sections = await adminLearningService.getAllSections();

        const perSection = await Promise.all(
            sections.map(async (section) => {
                const lessons = await adminLearningService.getLessonsInSection(section.uid);
                const perLesson = await Promise.all(
                    lessons.map(async (lesson) => {
                        const pages = await adminLearningService.getPagesInLesson(
                            section.uid,
                            lesson.uid,
                        );
                        return pages
                            .filter(isQuestionPage)
                            .filter((page) => page.shouldSaveAnswer)
                            .map((page) => ({ section, lesson, page }));
                    }),
                );
                return perLesson.flat();
            }),
        );

        return perSection.flat();
    }

    /**
     * One collectionGroup pass over every user's saved answers.
     * Returns questionUid -> { respondents, answer-string -> count }.
     */
    private async aggregateAnswers(): Promise<Map<string, QuestionAggregate>> {
        const snapshot = await getDocs(collectionGroup(db, 'learning_questions'));
        const byQuestion = new Map<string, QuestionAggregate>();

        snapshot.docs.forEach((docSnap) => {
            const questionUid = docSnap.id;
            const data = docSnap.data();
            const answers: unknown = data.answers;
            if (!Array.isArray(answers)) return;

            let aggregate = byQuestion.get(questionUid);
            if (!aggregate) {
                aggregate = { respondents: 0, counts: new Map<string, number>() };
                byQuestion.set(questionUid, aggregate);
            }

            aggregate.respondents += 1;
            answers.forEach((answer) => {
                if (typeof answer !== 'string') return;
                aggregate!.counts.set(answer, (aggregate!.counts.get(answer) || 0) + 1);
            });
        });

        return byQuestion;
    }

    private buildQuestionStats(
        page: LearningLessonQuestionPage,
        aggregate: QuestionAggregate | undefined,
    ): QuestionStats {
        const respondents = aggregate?.respondents ?? 0;
        const counts = new Map(aggregate?.counts ?? []);
        const hasCorrectAnswer = page.correctOptionIds.length > 0;
        const share = (count: number): number => (respondents > 0 ? count / respondents : 0);

        const options: OptionStat[] = page.options.map((option) => {
            // Match against both the AI-friendly label and the raw text to tolerate drift.
            const primary = optionLabel(option);
            let count = counts.get(primary) ?? 0;
            counts.delete(primary);
            if (option.text !== primary && counts.has(option.text)) {
                count += counts.get(option.text) ?? 0;
                counts.delete(option.text);
            }
            return {
                label: option.text,
                count,
                percent: share(count),
                isCorrect: hasCorrectAnswer && page.correctOptionIds.includes(option.uid),
            };
        });

        // Anything left in `counts` was answered but matches no current option.
        const legacyAnswers: OptionStat[] = Array.from(counts.entries())
            .map(([label, count]) => ({ label, count, percent: share(count), isCorrect: false }))
            .sort((a, b) => b.count - a.count);

        return {
            questionUid: page.uid,
            questionText: page.aiFriendlyQuestion?.trim() || page.content,
            respondents,
            hasCorrectAnswer,
            options,
            legacyAnswers,
        };
    }
}

interface QuestionAggregate {
    respondents: number;
    counts: Map<string, number>;
}

export const learningQuestionsStatsService = LearningQuestionsStatsService.getInstance();
